import React, { useRef, useCallback } from 'react'
import { IconButton } from '../../../ui'

function autoResize(el) {
  if (!el) return
  el.style.height = 'auto'
  el.style.height = el.scrollHeight + 'px'
}

export function BulletList({ items = [], onChange, maxItems = 10, maxChars = 140, placeholder = '', disabled = false, numbered = false }) {
  const inputRefs = useRef([])
  const setRef = useCallback((el, index) => {
    inputRefs.current[index] = el
    autoResize(el)
  }, [])

  function handleChange(index, val) {
    const next = [...items]
    next[index] = val
    onChange({ items: next })
  }

  function handleKeyDown(e, index) {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (items.length < maxItems) {
        const next = [...items]
        next.splice(index + 1, 0, '')
        onChange({ items: next })
        setTimeout(() => inputRefs.current[index + 1]?.focus(), 0)
      }
    } else if (e.key === 'Backspace' && items[index] === '' && items.length > 1) {
      e.preventDefault()
      const next = items.filter((_, i) => i !== index)
      onChange({ items: next })
      setTimeout(() => inputRefs.current[Math.max(0, index - 1)]?.focus(), 0)
    }
  }

  function handleBlur(index) {
    // remove empty items except the last one
    if (items[index] === '' && index < items.length - 1) {
      onChange({ items: items.filter((_, i) => i !== index) })
    }
  }

  function handleAdd() {
    if (items.length < maxItems) {
      onChange({ items: [...items, ''] })
      setTimeout(() => inputRefs.current[items.length]?.focus(), 0)
    }
  }

  function handleRemove(index) {
    if (items.length <= 1) {
      onChange({ items: [''] })
    } else {
      onChange({ items: items.filter((_, i) => i !== index) })
    }
  }

  const displayItems = items.length === 0 ? [''] : items

  return (
    <div style={styles.wrap}>
      {displayItems.map((item, index) => (
        <div key={index} style={styles.row}>
          <span style={styles.prefix}>{numbered ? `${index + 1}.` : '–'}</span>
          <div style={styles.inputWrap}>
            <textarea
              ref={(el) => setRef(el, index)}
              style={styles.input}
              value={item}
              rows={1}
              placeholder={disabled ? '' : placeholder}
              maxLength={maxChars}
              disabled={disabled}
              onChange={(e) => { handleChange(index, e.target.value); autoResize(e.target) }}
              onKeyDown={(e) => handleKeyDown(e, index)}
              onBlur={() => handleBlur(index)}
              aria-label={`Item ${index + 1}`}
            />
          </div>
          {!disabled && items.length > 0 && (
            <IconButton ariaLabel="Remove item" onClick={() => handleRemove(index)} tabIndex={-1}>×</IconButton>
          )}
        </div>
      ))}
      {!disabled && displayItems.length < maxItems && (
        <button style={styles.addBtn} onClick={handleAdd}>+ add</button>
      )}
    </div>
  )
}

const styles = {
  wrap: { padding: '8px 14px 12px', display: 'flex', flexDirection: 'column', gap: '4px' },
  row: { display: 'flex', alignItems: 'flex-start', gap: '6px' },
  prefix: { fontSize: '12px', color: 'var(--text-secondary)', opacity: 0.5, minWidth: '14px', flexShrink: 0, lineHeight: '1.5', paddingTop: '2px' },
  inputWrap: { flex: 1, position: 'relative' },
  input: {
    width: '100%', border: 'none', outline: 'none', background: 'transparent',
    fontSize: '12px', color: 'var(--text-primary)', lineHeight: '1.5',
    padding: '2px 0', resize: 'none', overflow: 'hidden', fontFamily: 'inherit',
  },
  addBtn: {
    background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px',
    color: '#3b82c4', padding: '2px 0', marginLeft: '20px', marginTop: '2px',
    textAlign: 'left',
  },
}
